<template>
  <div
    class="border rounded overflow-hidden"
    :class="{
      'border-green-600': hasPendingApprovalRequest || (canManage && hasApprovalRequests),
    }"
  >
    <div class="bg-white flex flex-col divide-y">
      <div class="inline-flex px-4 h-12 items-center space-x-2">
        <slot :title="name">
          <span class="inline-block truncate text-sm">
            {{ name }}
          </span>
        </slot>
      </div>
      <AssetableCover v-if="asset" :asset="asset">
        <ProjectAssetTileApprovalStatus
          v-if="hasApprovalRequests || hasApprovalClaims"
          :claims="projectAsset.approvalClaimsCount"
          :total="projectAsset.approvalRequestsCount"
          :pending="projectAsset.pendingApprovalRequestsCount"
          :approved="projectAsset.approvedApprovalRequestsCount"
          :rejected="projectAsset.rejectedApprovalRequestsCount"
          class="absolute top-0 left-0 mt-4 ml-4"
        />
      </AssetableCover>
      <div class="divide-y">
        <div v-if="displayConfirmationMessage" class="px-4 py-3 space-x-2">
          <BaseButton ghost :disabled="active" @click="displayConfirmationMessage = false">
            Cancel
          </BaseButton>
          <BaseButton danger :disabled="active" @click="deleteProjectAsset"> Confirm </BaseButton>
        </div>
        <template v-else>
          <div
            v-if="hasPendingApprovalRequest || canManage"
            class="flex space-x-2 bg-gray-100 px-4 py-3"
          >
            <BaseButton
              v-if="hasPendingApprovalRequest"
              success
              small
              class="flex-auto"
              @click="handleReview"
            >
              Review
            </BaseButton>
            <ProjectAssetTileManageReviewsAction v-if="canManage" :project-asset="projectAsset" />
          </div>
          <div
            v-if="projectTaskIsFromThisProject"
            class="flex flex-col space-y-2 bg-gray-100 px-4 py-3"
          >
            <BaseButton ghost small @click="viewTask"> View Task </BaseButton>
          </div>
          <div class="flex">
            <BaseButton v-if="url" ghost squared @click="copyUrl" class="flex-1 text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="inline-block w-4 h-4 focus:outline-none"
                v-tip="{ content: 'copy to clipboard' }"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path
                  d="M7 6V3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v14a1
                  1 0 0 1-1 1h-3v3c0 .552-.45 1-1.007 1H4.007A1.001
                  1.001 0 0 1 3 21l.003-14c0-.552.45-1 1.007-1H7zM5.003
                  8L5 20h10V8H5.003zM9 6h8v10h2V4H9v2z"
                />
              </svg>
            </BaseButton>
            <template v-if="canManage">
              <BaseButton
                ghost
                squared
                @click="$emit('edit', projectAsset)"
                class="flex-1 text-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="inline-block w-4 h-4 focus:outline-none"
                  v-tip="{ content: 'edit collection' }"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path
                    d="M12.414 5H21a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1
                    1 0 0 1-1-1V4a1 1 0 0 1 1-1h7.414l2
                    2zM4 5v14h16V7h-8.414l-2-2H4zm4.591 8.809a3.508
                    3.508 0 0 1 0-1.622l-.991-.572 1-1.732.991.573a3.495 3.495
                    0 0 1 1.404-.812V8.5h2v1.144c.532.159 1.01.44
                    1.403.812l.992-.573 1 1.731-.991.573a3.508 3.508
                    0 0 1 0 1.622l.991.572-1 1.731-.991-.572a3.495 3.495
                    0 0 1-1.404.811v1.145h-2V16.35a3.495 3.495 0 0
                    1-1.404-.811l-.991.572-1-1.73.991-.573zm3.404.688a1.5
                    1.5 0 1 0 0-2.998 1.5 1.5 0 0 0 0 2.998z"
                  />
                </svg>
              </BaseButton>
              <BaseButton
                v-if="link"
                ghost
                squared
                @click="$emit('edit-link', projectAsset)"
                class="flex-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="inline-block w-4 h-4 focus:outline-none"
                  v-tip="{ content: 'edit asset' }"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path
                    d="M6.414 16L16.556 5.858l-1.414-1.414L5 14.586V16h1.414zm.829
                    2H3v-4.243L14.435 2.322a1 1 0 0 1 1.414 0l2.829 2.829a1
                    1 0 0 1 0 1.414L7.243 18zM3 20h18v2H3v-2z"
                  />
                </svg>
              </BaseButton>
              <BaseButton
                v-if="note"
                ghost
                squared
                @click="$emit('edit-note', projectAsset)"
                class="flex-1"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="inline-block w-4 h-4 focus:outline-none"
                  v-tip="{ content: 'edit asset' }"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path
                    d="M6.414 16L16.556 5.858l-1.414-1.414L5 14.586V16h1.414zm.829
                    2H3v-4.243L14.435 2.322a1 1 0 0 1 1.414 0l2.829 2.829a1
                    1 0 0 1 0 1.414L7.243 18zM3 20h18v2H3v-2z"
                  />
                </svg>
              </BaseButton>
              <BaseButton ghost squared @click="confirmDelete" class="flex-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  class="inline-block w-4 h-4 focus:outline-none"
                  v-tip="{ content: 'delete' }"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path
                    d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1
                    1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zM9 4v2h6V4H9z"
                  />
                </svg>
              </BaseButton>
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import copyToClipboard from '@/utils/copy-to-clipboard';
import ProjectAssetTileApprovalStatus from './ProjectAssetTileApprovalStatus.vue';
import ProjectAssetTileManageReviewsAction from './ProjectAssetTileManageReviewsAction.vue';
import AssetableCover from './AssetableCover.vue';

export default {
  name: 'ProjectAssetTile',

  components: {
    ProjectAssetTileApprovalStatus,
    ProjectAssetTileManageReviewsAction,
    AssetableCover,
  },

  props: {
    projectAsset: {
      type: Object,
      required: true,
    },
    canManage: Boolean,
    onProjectAssetDelete: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      active: false,
      displayConfirmationMessage: false,
    };
  },

  computed: {
    conditionalClick() {
      if (this.url || this.link) return {};

      return {
        click: () => {
          this.$emit('view-note', this.projectAsset);
        },
      };
    },
    hasApprovalRequests() {
      if (!this.projectAsset.approvalRequestsCount) {
        return false;
      }

      return this.projectAsset.approvalRequestsCount > 0;
    },
    hasApprovalClaims() {
      if (!this.projectAsset.approvalClaimsCount) {
        return false;
      }

      return this.projectAsset.approvalClaimsCount > 0;
    },
    hasPendingApprovalRequest() {
      if (!this.projectAsset.pendingApprovalRequest) {
        return false;
      }

      return true;
    },
    asset() {
      return this.$store.state.entities.assets.byId[this.projectAsset.assetId];
    },
    hasProjectTask() {
      return (
        typeof this.asset?.projectTask === 'object' &&
        this.asset.projectTask !== null &&
        this.asset.projectTask.task
      );
    },
    projectTaskIsFromThisProject() {
      return (
        this.hasProjectTask && this.asset.projectTask.projectId === this.projectAsset.projectId
      );
    },
    media() {
      if (!this.asset?.media) {
        return null;
      }

      return this.$store.state.entities.media.byId[this.asset.media];
    },
    link() {
      if (!this?.asset?.link) {
        return null;
      }

      return this.$store.state.entities.links.byId[this.asset.link];
    },
    note() {
      if (!this?.asset?.note) {
        return null;
      }

      return this.$store.state.entities.notes.byId[this.asset.note];
    },
    name() {
      if (this.media) {
        return this.media.name;
      }

      if (this.link) {
        return this.link.title || this.link.url;
      }

      return 'Note';
    },
    url() {
      if (this.media) {
        return this.media.url;
      }

      if (this.link) {
        return this.link.url;
      }

      return '';
    },
  },

  methods: {
    copyUrl() {
      copyToClipboard(this.url);
    },
    handleReview() {
      this.$emit('review', this.projectAsset);
    },
    viewTask() {
      this.$modal.show({
        component: 'ProjectTaskModal',
        props: {
          taskId: this.asset.projectTask.taskId,
        },
        on: {},
        hideEvent: 'task-modal:closed',
      });
    },
    confirmDelete() {
      this.displayConfirmationMessage = true;
      setTimeout(() => {
        if (this.active) {
          return;
        }
        this.displayConfirmationMessage = false;
      }, 3000);
    },
    deleteProjectAsset() {
      if (typeof this.onProjectAssetDelete === 'function') {
        this.active = true;
        this.onProjectAssetDelete(this.projectAsset)
          .then(() => {
            this.removeAsset();
          })
          .catch(() => {
            this.displayConfirmationMessage = false;
            this.active = false;
          });

        return;
      }

      this.removeAsset();
    },

    removeAsset() {
      this.$store.commit('ui/projectAssets/setIds', [
        ...this.$store.state.ui.projectAssets.ids.filter((id) => id !== this.projectAsset.id),
      ]);
    },
  },
};
</script>
